/* eslint-disable max-len */
import { Column, Option } from '@access-erp/common';
import {
  CellClickedEvent,
  CellType,
  FilterValueTypes,
} from '@access-erp/ui-components';
import { AlertTriangle } from 'lucide-react';
import { Tooltip } from '@tag/tag-components-react-v4';

import { shouldSkipCellClickNavigation } from '@/components/common/EnquiriesScreen/components/LinkCellRenderer';
import { ContractNumberRenderer } from '../components/ContractNumberRenderer';
import { CellFormatters } from '@/components/common/EnquiriesScreen/utils/CellFormatters';
import { guidFilterOptions } from '@/components/common/EnquiriesScreen/utils/filters.config';
import { filtersforStringifiedNumbers } from '@/components/common/EnquiriesScreen/utils/filtersForStringigfiedNumbers';
import { billingBCPath } from '@/utils/path.utlis';
import { billingTypes, extractSubMenuStatusFilters, salesOrderTypesFormatted, salesOrderSourceTypes } from './filters.config';
import { SalesOrderType } from '@/graphql/contracts/_generated_/contracts';

const VISIBLE_REASON_CHARS_LENGTH = 50;

export interface UseTransactionTableColumnsReturn {
  headerColumns: Column<any>[];
}

type DefaultColumnArgsType = {
  getContractLink: (contractId: string, searchString?: string) => string;
  goToContract: (contractLink: string) => void;
  billingIntervalFIlterOption: Option[];
  isBillingCyclesPath: boolean;
};

export const defaultColumns = ({
  getContractLink,
  goToContract,
  billingIntervalFIlterOption,
  isBillingCyclesPath,
}: DefaultColumnArgsType): Column<any>[] => [
  {
    name: 'contractNumber',
    caption: 'Contract Number',
    width: 200,
    cellType: CellType.String,
    cellRenderer: (params) => {
      return (
          <ContractNumberRenderer
            data={params.data}
            getContractLink={getContractLink}
            searchString={params.context?.serverSide?.current?.search}
          />
      );
    },
    onCellClicked: (e: CellClickedEvent) => {
      const contracts = e.data?.contracts ?? [];
      if (contracts.length > 1) return;
      if (shouldSkipCellClickNavigation(e)) {
        return;
      }
      const searchStringIfAny = e.context?.serverSide?.current?.search;
      const contractId = e.data.contracts?.[0]?.id ?? '';
      goToContract(getContractLink(contractId, searchStringIfAny));
    },
    defaultFilter: 1,
  },
  {
    name: 'consolidated',
    caption: 'Consolidated',
    cellType: CellType.Bool,
    width: 150,
    defaultFilter: 2,
  },
  {
    name: 'customerAccountNumber',
    caption: 'Account',
    cellType: CellType.String,
    valuePath: 'billedCustomer.customerAccountNumber',
    width: 220,
    defaultFilter: 3,
  },
  {
    name: 'customerName',
    caption: 'Account Name',
    width: 150,
    cellType: CellType.String,
    valuePath: 'billedCustomer.customerName',
    defaultFilter: 4,
  },
  {
    valuePath: 'referenceSalesOrderID',
    name: 'referenceSalesOrderID',
    caption: 'SO Number',
    cellType: CellType.String,
    defaultFilter: 5,
  },
  {
    name: 'salesOrderID',
    caption: 'SO ID',
    cellType: CellType.String,
    defaultFilter: 6,
    filterType: FilterValueTypes.StringFilter,
    filterOptions: {
      availableOptions: guidFilterOptions,
    },
  },
  {
    valuePath: 'billingInterval',
    name: 'billingIntervals',
    caption: 'Billing Interval',
    cellType: CellType.String,
    filterType: FilterValueTypes.EnumFilter,
    filterData: billingIntervalFIlterOption,
    defaultFilter: 7,
    cellFormatter: ({ value }) => {
      return value?.join(', ') ?? '';
    },
  },
  {
    valuePath: 'invoiceDate',
    name: 'invoiceDate',
    caption: 'Invoice Date',
    cellType: CellType.Date,
    cellFormatter: CellFormatters.date,
    width: 120,
    defaultFilter: 8,
  },
  {
    valuePath: 'created',
    name: 'created',
    caption: 'Created Date',
    cellType: CellType.Date,
    cellFormatter: CellFormatters.date,
    width: 120,
    defaultFilter: 9,
  },
  {
    valuePath: 'amount',
    name: 'amount',
    caption: 'Amount',
    cellType: CellType.String,
    cellFormatter: (params) => CellFormatters.currency(params, params?.data?.currency ?? ''),
    filterType: FilterValueTypes.StringFilter,
    filterOptions: {
      availableOptions: filtersforStringifiedNumbers,
    },
    defaultFilter: 10,
    cellStyle: { justifyContent: 'end' },
  },
  {
    valuePath: 'status.formattedStatus',
    name: 'salesOrderStatus',
    caption: 'Status',
    cellType: CellType.String,
    filterType: FilterValueTypes.EnumFilter,
    filterData: () => extractSubMenuStatusFilters(window.location.pathname.includes(billingBCPath)),
    defaultFilter: 11,
    cellStyle: (params: any) => {
      if (params?.value === 'Created') {
        return { backgroundColor: '#d4edda' };
      }
      return undefined;
    },
  },
  {
    valuePath: 'statusMessage',
    name: 'statusMessage',
    caption: 'Reason for failure',
    cellType: CellType.String,
    width: 370,
    cellRenderer: ({ value }) => {
      const reasonForFailureTrimmed =
          value && value.length > VISIBLE_REASON_CHARS_LENGTH
            ? value.slice(0, VISIBLE_REASON_CHARS_LENGTH) + '...'
            : value || '';

      const titlePopUp = value && value.length > VISIBLE_REASON_CHARS_LENGTH ? value : undefined;

      return <div title={titlePopUp}>{reasonForFailureTrimmed}</div>;
    },
    defaultFilter: 12,
  },
  {
    name: 'type',
    caption: 'Type',
    cellType: CellType.String,
    filterType: FilterValueTypes.EnumFilter,
    filterData: billingTypes,
    defaultFilter: 13,
    cellFormatter: ({ value }) => salesOrderTypesFormatted[value] ?? value,
  },

  ...((isBillingCyclesPath
    ? [
      {
        name: 'sentDate',
        caption: 'Sent Date',
        cellType: CellType.Date,
        cellFormatter: CellFormatters.date,
        width: 120,
        defaultFilter: 16,
      },
      {
        name: 'source',
        caption: 'Source',
        cellType: CellType.String,
        filterType: FilterValueTypes.EnumFilter,
        filterData: salesOrderSourceTypes,
        width: 100,
        defaultFilter: 17,
      },
      {
        name: 'isAbandoned',
        caption: 'Is Abandoned',
        cellType: CellType.Bool,
        width: 120,
        defaultFilter: 18,
      },
      {
        name: 'reasonCode',
        caption: 'Reason Code',
        cellType: CellType.String,
        width: 150,
        defaultFilter: 19,
      },
      {
        name: 'comment',
        caption: 'Comment',
        cellType: CellType.String,
        width: 200,
        defaultFilter: 20,
      },
      {
        valuePath: 'soStartDate',
        name: 'sOStartDate',
        caption: 'SO Start Date',
        cellType: CellType.Date,
        cellFormatter: CellFormatters.date,
        width: 150,
        defaultFilter: 21,
        cellRenderer: (params: any) => {
          const formatted = CellFormatters.date(params);
          if (!formatted) return null;
          const hasMixed = params?.data?.hasMixedSoStartDate;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>{formatted}</span>
              {hasMixed && (
                <Tooltip
                  tooltipFor={
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      <AlertTriangle size={14} color="#e6a817" />
                    </span>
                  }
                >
                  Mixed Start Dates, displaying smallest.
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        valuePath: 'soEndDate',
        name: 'sOEndDate',
        caption: 'SO End Date',
        cellType: CellType.Date,
        cellFormatter: CellFormatters.date,
        width: 150,
        defaultFilter: 22,
        cellRenderer: (params: any) => {
          const formatted = CellFormatters.date(params);
          if (!formatted) return null;
          const hasMixed = params?.data?.hasMixedSoEndDate;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>{formatted}</span>
              {hasMixed && (
                <Tooltip
                  tooltipFor={
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      <AlertTriangle size={14} color="#e6a817" />
                    </span>
                  }
                >
                  Mixed End Dates, displaying largest.
                </Tooltip>
              )}
            </div>
          );
        },
      },
    ]
    : []) as Column<SalesOrderType>[]),
];

export const getHeaderColumns = (defaultColumnArgs: DefaultColumnArgsType): Column<any>[] =>
  defaultColumns(defaultColumnArgs);
